import React, { useEffect, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { get, post, put } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, Modal } from "../../../components/admin/EssUi";
import { BarChart3, Search } from "lucide-react";



const AdminPanelLeavesPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageLeaves;
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ employee_id: "", holiday_status_id: "", date_from: "", date_to: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const PAGE_SIZE = 15;
  useEffect(() => { setPage(1); }, [search, deptFilter, typeFilter, statusFilter, activeTab]);

  const loadData = useCallback(async () => {
    try {
      const [leaveRes, empRes, deptRes] = await Promise.all([
        get("/api/leave-requests"),
        get("/api/employees?limit=500"),
        get("/api/departments"),
      ]);
      const leaveRows = leaveRes?.data || [];
      const empRows = empRes?.data || [];
      const deptRows = deptRes?.data || [];
      setEmployees(empRows);
      const empMap = {};
      empRows.forEach((e) => { empMap[e.id] = e; });
      const deptMap = {};
      deptRows.forEach((d) => { deptMap[d.id] = d.name || d.complete_name; });
      setData(leaveRows.map((e) => {
        const emp = empMap[e.employee_id] || {};
        const status = e.state === "APPROVED" ? "Approved" : e.state === "REFUSED" ? "Refused" : e.state === "DRAFT" || e.state === "PENDING" ? "Pending" : e.state;
        return {
          id: e.id,
          employee: e.employee_name || emp.name || "—",
          department: emp.department_id ? (deptMap[emp.department_id] || emp.department_id) : "—",
          leaveType: e.leave_type_name || e.holiday_status_id || "—",
          start: e.date_from || "—",
          end: e.date_to || "—",
          days: Math.round(Number(e.number_of_days) || 0),
          applied: e.created_at ? new Date(e.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—",
          status: status,
          raw: e,
        };
      }));
    } catch { setData([]); }
    setLoaded(true);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAction = async (newState) => {
    if (!detailRow) return;
    setBusy(true);
    try {
      const body = { state: newState };
      if (comment.trim()) body.ess_manager_comment = comment.trim();
      await put(`/api/leave-requests/${detailRow.id}`, body);
      setDetailRow(null);
      setComment("");
      setLoaded(false);
      await loadData();
    } catch (e) {
      alert("Action failed: " + (e.message || "Unknown"));
    }
    setBusy(false);
  };

  const handleCreate = async () => {
    if (!newForm.employee_id || !newForm.holiday_status_id || !newForm.date_from || !newForm.date_to) {
      alert("Please fill employee, leave type and both dates.");
      return;
    }
    setSubmitting(true);
    try {
      const start = new Date(newForm.date_from);
      const end = new Date(newForm.date_to);
      const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
      await post("/api/leave-requests", {
        employee_id: Number(newForm.employee_id),
        holiday_status_id: Number(newForm.holiday_status_id),
        date_from: `${newForm.date_from}T00:00:00Z`,
        date_to: `${newForm.date_to}T00:00:00Z`,
        number_of_days: days,
        name: (newForm.reason || "Leave request").trim(),
        private_name: (newForm.reason || "Leave request").trim(),
      });
      setNewOpen(false);
      setNewForm({ employee_id: "", holiday_status_id: "", date_from: "", date_to: "", reason: "" });
      setLoaded(false);
      await loadData();
    } catch (e) {
      alert("Create failed: " + (e.message || "Unknown"));
    }
    setSubmitting(false);
  };

  if (!loaded) return <LoadingScreen />;

  const filtered = data.filter(r => {
    if (activeTab !== "all" && r.status.toLowerCase() !== activeTab) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (deptFilter !== "all" && r.department !== deptFilter) return false;
    if (typeFilter !== "all" && r.leaveType !== typeFilter) return false;
    if (search.trim() && !r.employee.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const depts = [...new Set(data.map(r=>r.department))].filter(d => d !== "—");
  const types = [...new Set(data.map(r=>r.leaveType))].filter(t => t !== "—");
  const leaveTypes = [...new Map(data.filter(r => r.raw?.holiday_status_id != null).map(r => [Number(r.raw.holiday_status_id), r.leaveType])).entries()];
  const activeEmployees = employees.filter((e) => e.active !== false);

  const tabs = [
    { key: "all", label: "All Requests", count: data.length },
    { key: "pending", label: "Pending", count: data.filter(r=>r.status==="Pending").length },
    { key: "approved", label: "Approved", count: data.filter(r=>r.status==="Approved").length },
    { key: "refused", label: "Refused", count: data.filter(r=>r.status==="Refused").length },
    { key: "today", label: "Today", count: data.filter(r => {
      const today = new Date().toISOString().split('T')[0];
      return r.start === today || r.end === today || (r.start <= today && r.end >= today);
    }).length },
  ];

  const getStatusColor = (status) => {
    const colors = {
      "Pending": "#d97706",
      "Approved": "#16a34a",
      "Refused": "#e11d48"
    };
    return colors[status] || "#64748b";
  };

  const getStatusBg = (status) => {
    const colors = {
      "Pending": "#fef3c7",
      "Approved": "#dcfce7",
      "Refused": "#fecdd3"
    };
    return colors[status] || "#f1f5f9";
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "—") return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader 
        title="Leave Management" 
        subtitle="Review, approve and track all employee leaves"
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
            <button onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}>
              ＋ New Leave Request
            </button>
            )}
            <button onClick={() => navigate("/admin-panel/reports")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}>
              <BarChart3 size={16} />
              Reports
            </button>
          </div>
        }
      />

      {(() => {
        const pending = data.filter((r) => r.status === "Pending").length;
        const approved = data.filter((r) => r.status === "Approved").length;
        const td = new Date().toISOString().split("T")[0];
        const onLeave = data.filter((r) => r.start === td || r.end === td || (r.start <= td && r.end >= td)).length;
        const totalDays = data.reduce((s, r) => s + (r.days || 0), 0);
        const avgBalance = data.length > 0 ? `${(totalDays / data.length).toFixed(1)}d` : "0d";
        return (
        <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
          <StatCard label="Pending Approval" value={String(pending)} icon="⏳" color="amber" trend={`${pending} require action`} />
          <StatCard label="Approved" value={String(approved)} icon="✅" color="green" trend="Total approved" />
          <StatCard label="On Leave Today" value={String(onLeave)} icon="🏖️" color="blue" trend={`${data.length > 0 ? Math.round((onLeave / data.length) * 100) : 0}% of requests`} />
          <StatCard label="Avg Leave Duration" value={avgBalance} icon="📊" color="indigo" trend="Per request" />
        </div>
        );
      })()}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: activeTab === tab.key ? '#059669' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#475569',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ 
                background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '12px', 
        marginBottom: '24px',
        background: 'white',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', position: 'relative' }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee, leave type"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              background: '#f8fafc'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <select 
          value={typeFilter} 
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#f8fafc',
            outline: 'none',
            cursor: 'pointer',
            flex: '1 1 160px',
            minWidth: '160px'
          }}
        >
          <option value="all">All Leave Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <select 
          value={deptFilter} 
          onChange={(e) => setDeptFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#f8fafc',
            outline: 'none',
            cursor: 'pointer',
            flex: '1 1 160px',
            minWidth: '160px'
          }}
        >
          <option value="all">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#f8fafc',
            outline: 'none',
            cursor: 'pointer',
            flex: '1 1 160px',
            minWidth: '160px'
          }}
        >
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Refused">Refused</option>
        </select>
          <input
            type="date"
            defaultValue="2026-06-25"
            style={{
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#f8fafc',
              outline: 'none',
              cursor: 'pointer',
              flex: '1 1 140px',
              minWidth: '140px'
            }}
          />
        </div>
      </div>

      {/* Main Content - Grid Layout */}
      <div style={{ display: 'grid', gap: '24px' }} className="ess-rg-2-1">
        {/* Table Section */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {["Employee", "Department", "Leave Type", "Start Date", "End Date", "Days", "Applied On", "Status", ""].map((h) => (
                    <th key={h} style={{ textAlign: h === "Days" ? 'center' : h === "" ? 'right' : 'left', padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No leave requests found</td></tr>
                ) : (paginatedData.map((row, index) => (
                  <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '500', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }} title={row.employee}>{row.employee}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '130px' }} title={row.department}>{row.department}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '500', color: '#475569', whiteSpace: 'nowrap' }}>{row.leaveType}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(row.start)}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(row.end)}</td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: '#1e293b', textAlign: 'center', whiteSpace: 'nowrap' }}>{row.days}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.applied}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>{row.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setDetailRow(row); setComment(""); }} style={{ border: 'none', background: 'transparent', color: '#4f46e5', fontSize: '12px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}>View</button>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: page <= 1 ? '#f8fafc' : 'white', color: page <= 1 ? '#cbd5e1' : '#059669', cursor: page <= 1 ? 'default' : 'pointer', fontSize: '12px', fontWeight: '500' }}>‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} style={{ padding: '5px 10px', border: 'none', borderRadius: '6px', background: p === page ? '#059669' : 'transparent', color: p === page ? 'white' : '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: p === page ? '600' : '400', minWidth: '28px' }}>{p}</button>
              ))}
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: page >= totalPages ? '#f8fafc' : 'white', color: page >= totalPages ? '#cbd5e1' : '#059669', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: '12px', fontWeight: '500' }}>Next ›</button>
              <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>{filtered.length} total</span>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Leave Distribution by Type</h3>
            {(() => {
              const byType = {};
              data.forEach((r) => {
                const t = r.leaveType || "Other";
                byType[t] = (byType[t] || 0) + (r.days || 0);
              });
              const sorted = Object.entries(byType).sort((a, b) => b[1] - a[1]);
              const colors = ["#4f46e5", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#9333ea", "#ea580c", "#65a30d"];
              return sorted.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: "13px" }}>No data</div>
              ) : (
                sorted.map(([type, days], i) => (
                  <div key={type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: colors[i % colors.length] }} />
                      <span style={{ fontSize: "13px", color: "#475569" }}>{type}</span>
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>{Math.round(days)}d</span>
                  </div>
                ))
              );
            })()}
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Department Leave Distribution</h3>
            {(() => {
              const byDept = {};
              data.forEach((r) => {
                const d = r.department || "Unknown";
                byDept[d] = (byDept[d] || 0) + (r.days || 0);
              });
              const sorted = Object.entries(byDept).sort((a, b) => b[1] - a[1]);
              const maxVal = sorted.length > 0 ? Math.max(...sorted.map(([, v]) => v)) : 1;
              const colors = ["#4f46e5", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#9333ea", "#ea580c"];
              return sorted.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: "13px" }}>No data</div>
              ) : (
                sorted.map(([dept, days], i) => (
                  <div key={dept} style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                      <span style={{ color: "#64748b" }}>{dept}</span>
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>{Math.round(days)}d</span>
                    </div>
                    <div style={{ height: 6, background: "#f1f5f9", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(days / maxVal) * 100}%`, background: colors[i % colors.length], borderRadius: "3px", transition: "width 1s ease" }} />
                    </div>
                  </div>
                ))
              );
            })()}
          </div>
        </div>
      </div>

      <Modal open={!!detailRow} onClose={() => { setDetailRow(null); setComment(""); }} title="Leave Request Details" size="lg">
        {detailRow && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>{detailRow.employee}</p>
                <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{detailRow.department}</p>
              </div>
              <span style={{ background: getStatusBg(detailRow.status), color: getStatusColor(detailRow.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>{detailRow.status}</span>
            </div>

            {[
              { label: "Leave Type", value: detailRow.leaveType },
              { label: "Start Date", value: formatDate(detailRow.start) },
              { label: "End Date", value: formatDate(detailRow.end) },
              { label: "Number of Days", value: String(detailRow.days) },
              { label: "Applied On", value: detailRow.applied },
              { label: "Manager Comment", value: detailRow.raw?.ess_manager_comment || "—" },
            ].map((row) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748b' }}>{row.label}</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{row.value}</span>
              </div>
            ))}

            {detailRow.status === "Pending" && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Manager Comment (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note for the employee..."
                  rows={2}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#f8fafc', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              <button onClick={() => { setDetailRow(null); setComment(""); }} className="btn" style={{ background: '#f1f5f9', color: '#64748b', border: 'none', cursor: 'pointer' }}>Close</button>
              {canManage && detailRow.status === "Pending" && (
                <>
                  <button onClick={() => handleAction("REFUSED")} disabled={busy} className="btn" style={{ background: '#fef2f2', color: '#e11d48', border: '1px solid #fecdd3', cursor: 'pointer' }}>{busy ? "Working..." : "✕ Reject"}</button>
                  <button onClick={() => handleAction("APPROVED")} disabled={busy} className="btn" style={{ background: '#059669', color: 'white', border: 'none', cursor: 'pointer' }}>{busy ? "Working..." : "✓ Approve"}</button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={newOpen} onClose={() => { setNewOpen(false); setNewForm({ employee_id: "", holiday_status_id: "", date_from: "", date_to: "", reason: "" }); }} title="＋ New Leave Request" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Employee *</label>
            <select className="ess-inp" style={{ width: '100%' }} value={newForm.employee_id} onChange={(e) => setNewForm({ ...newForm, employee_id: e.target.value })}>
              <option value="">— Select —</option>
              {activeEmployees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Leave Type *</label>
            <select className="ess-inp" style={{ width: '100%' }} value={newForm.holiday_status_id} onChange={(e) => setNewForm({ ...newForm, holiday_status_id: e.target.value })}>
              <option value="">— Select —</option>
              {leaveTypes.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gap: '12px' }} className="ess-rg-2">
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>From *</label>
              <input type="date" className="ess-inp" style={{ width: '100%' }} value={newForm.date_from} onChange={(e) => setNewForm({ ...newForm, date_from: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>To *</label>
              <input type="date" className="ess-inp" style={{ width: '100%' }} value={newForm.date_to} onChange={(e) => setNewForm({ ...newForm, date_to: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Reason</label>
            <input className="ess-inp" style={{ width: '100%' }} value={newForm.reason} onChange={(e) => setNewForm({ ...newForm, reason: e.target.value })} placeholder="e.g. Annual leave" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
          <button onClick={() => { setNewOpen(false); setNewForm({ employee_id: "", holiday_status_id: "", date_from: "", date_to: "", reason: "" }); }} className="btn" style={{ background: '#f1f5f9', color: '#64748b', border: 'none', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleCreate} disabled={submitting} className="btn" style={{ background: submitting ? '#94a3b8' : '#059669', color: 'white', border: 'none', cursor: 'pointer' }}>
            {submitting ? "Creating..." : "＋ Create"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelLeavesPage;